import type { FormRLeafLine } from '../services/form-r/form-r-sections.ts';
import type { ChemicalEditing } from './chemical-types.ts';
import type { ProductID, ReleaseProfileID, ReleaseProfileQuestionID, UsageTypeID } from './db.ts';
import type { TRIQuantityBasisEstimationCodeType, TRIWasteManagementTypeCodeType } from './form-r.ts';
import type { PotentialEmissionsBasisPeriod, WasteStreamID } from './release-profiles.ts';

// ── Extraction modes ────────────────────────────────────────────────────────

export const EXTRACTION_MODES = ['products', 'releaseProfiles', 'assignment', 'questionnaire'] as const;

export type ExtractionMode = (typeof EXTRACTION_MODES)[number];

export const EXTRACTION_MODE_LABELS: Record<ExtractionMode, string> = {
  products: 'Products, Chemicals & Usage',
  releaseProfiles: 'Release & Threshold Profiles',
  assignment: 'Profile Assignments',
  questionnaire: 'Questionnaire Answers',
};

export const EXTRACTION_MODE_DESCRIPTIONS: Record<ExtractionMode, string> = {
  products:
    'Extract product names, CAS numbers, chemical concentrations, specific gravity, usage types, and usage quantities (pounds used).',
  releaseProfiles:
    'Extract release calculation formulas, variables, Form R line assignments, and any non-standard threshold profiles.',
  assignment: 'Map release profiles to specific processes and products at each facility.',
  questionnaire: 'Extract answers to release profile questionnaire variables.',
};

export const STEP_PREREQUISITES: Record<ExtractionMode, ExtractionMode[]> = {
  products: [],
  releaseProfiles: ['products'],
  assignment: ['products', 'releaseProfiles'],
  questionnaire: ['products', 'releaseProfiles', 'assignment'],
};

// ── Per-mode result types ───────────────────────────────────────────────────

export interface ProductsAgentResult {
  summary: string;
  products: AgentProduct[];
  warnings: string[];
  sheetAnalysis: Record<string, string>;
}

export interface ReleaseProfilesAgentResult {
  summary: string;
  releaseProfiles: AgentReleaseProfile[];
  warnings: string[];
  sheetAnalysis: Record<string, string>;
}

export interface AssignmentAgentResult {
  summary: string;
  assignments: AgentAssignment[];
  processNames: string[];
  warnings: string[];
  sheetAnalysis: Record<string, string>;
}

export interface AgentSection4MaxOnSite {
  method: 'inventory-backlog-facility-wide' | 'inventory-backlog-per-product' | 'per-product-max' | 'direct-entry';
  inventoryBacklogInWeeksOnSite?: number;
  perProductBacklogWeeks?: { productID: ProductID; backlogWeeks: number }[];
  perProductMaxQuantities?: { productID: ProductID; maxQuantityOnSite: number }[];
  chemicalPeakPounds?: Record<string, number>;
  sourceNote?: string;
}

export interface AgentReceptors {
  streams?: { name: string; reachCode?: string | null }[];
  potws?: {
    name: string;
    address?: string | null;
    city?: string | null;
    county?: string | null;
    state?: string | null;
    zip?: string | null;
    epaRegistryId?: string | null;
  }[];
  offSiteLocations?: {
    name: string;
    address?: string | null;
    city?: string | null;
    county?: string | null;
    state?: string | null;
    zip?: string | null;
    epaRegistryId?: string | null;
  }[];
}

export interface AgentProductionRatioValues {
  type: 'production' | 'activity' | 'N/A' | null;
  priorYearValue: number | '';
  currentYearValue: number | '';
}

export interface AgentProductionRatio extends AgentProductionRatioValues {
  isChemicalSpecific?: boolean;
  byChemical?: Record<string, AgentProductionRatioValues>;
}

export interface QuestionnaireAgentResult {
  summary: string;
  answers: AgentQuestionnaireAnswer[];
  unmatchedVariables: string[];
  warnings: string[];
  sheetAnalysis: Record<string, string>;
  maxOnSiteAmount?: AgentSection4MaxOnSite;
  receptors?: AgentReceptors;
  productionOrActivityRatio?: AgentProductionRatio;
}

export type SpreadsheetAgentResult =
  | ProductsAgentResult
  | ReleaseProfilesAgentResult
  | AssignmentAgentResult
  | QuestionnaireAgentResult;

// ── Data shape types ────────────────────────────────────────────────────────

export interface AgentProduct {
  name: string;
  specificGravity: number | null;
  usageUnits: 'gallons' | 'pounds';
  solidsPercentageByWeight: number | null;
  solidsPercentageByVolume: number | null;
  poundsUsed: number | null;
  rawValue: number | null;
  rawUnits: string | null;
  conversionNote: string | null;
  tags: string[];
  chemicals: AgentProductChemical[];
}

export type AgentProductChemical = Omit<ChemicalEditing, 'productChemicalCompositionID'>;

export interface AgentReleaseProfile {
  name: string;
  description: string;
  productAssignmentGuidance: string;
  formRLine: FormRLeafLine | '';
  basis: '' | 'C' | 'M' | 'M1' | 'M2' | 'E' | 'E1' | 'E2' | 'O';
  /** If set (0/1/2), this profile is used for threshold determination, not release calculation */
  thresholdUsageTypeID?: number | null;
  formula: {
    type: 'formulaElaborated';
    formulas: Record<string, AgentFormula>;
    choiceLists: Record<string, AgentChoiceList>;
  };
  variables: AgentVariable[];
}

export interface AgentAssignment {
  processName: string;
  releaseProfileName: string;
  releaseProfileID?: ReleaseProfileID;
  productName: string | null;
  productID?: ProductID;
  thresholdOrRelease: 'release' | 'threshold' | 'both';
  thresholdUsageTypeID: UsageTypeID | null;
}

export interface AgentQuestionnaireAnswer {
  releaseProfileName: string;
  variableNameForVariable: string;
  value: number;
  casOrCategory?: string;
  facilityName?: string;
  facilityID?: number;
  year?: number;
  basisCode?: '' | TRIQuantityBasisEstimationCodeType;
  wasteManagementCode?: TRIWasteManagementTypeCodeType;
  receptorName?: string;
  sourceCell?: string;
  sourceNote?: string;
}

export interface AgentFormula {
  formulaID: string;
  nameFriendly: string;
  nameForVariable: string;
  formula: string;
  outputs: Record<string, AgentFormulaOutput>;
}

export interface AgentFormulaOutput {
  outputID: string;
  ifChoices: { choiceListID: string; choiceID: string }[];
  type: 'actual' | 'potential';
  chemicals: 'inferred' | 'allVolatile' | 'allSolid' | string[];
  basis: string;
  /** Duration represented by one potential-emissions result. Only meaningful when type is 'potential'. */
  potentialBasisPeriod: PotentialEmissionsBasisPeriod;
}

export interface AgentChoiceList {
  choiceListID: string;
  question: string;
  choices: { choiceID: string; title: string }[];
}

export interface AgentVariable {
  nameFriendly: string;
  nameForVariable: string;
  description: string;
  sourceDescription: string;
  units: string;
  definition:
    | { type: 'constant'; value: number }
    | {
        type: 'question';
        questionID: ReleaseProfileQuestionID;
        isChemicalSpecific: boolean;
        isAutoFilledFromPriorYear: boolean;
        facilityProvidesSourceDocumentation: boolean;
      }
    | { type: 'chemicalProfile'; chemicals: { casOrCategory: string; value: number }[]; usageTypesApplied: number[] }
    | {
        type: 'wasteStream';
        wasteStreamID: WasteStreamID;
        isFromStormwater: boolean;
        source: { type: 'formula'; formula: string } | { type: 'questionnaire' };
      };
}

// ── Progress events ─────────────────────────────────────────────────────────

export type AgentProgressEvent =
  | { type: 'status'; message: string }
  | { type: 'agent_message'; text: string }
  | { type: 'tool_use'; toolName: string; input?: string }
  | { type: 'tool_result'; toolUseId: string; content?: string; isError?: boolean }
  | { type: 'thinking' }
  | { type: 'model_request_start' }
  | { type: 'model_request_end'; inputTokens: number; outputTokens: number; isError: boolean }
  | { type: 'session_error'; errorType: string; message: string; retryStatus: string }
  | { type: 'session_terminated' }
  | { type: 'upload_complete'; fileId: string }
  | { type: 'session_created'; sessionId: string; mountPath: string; refMountPaths?: Record<string, string> }
  | { type: 'result'; data: SpreadsheetAgentResult }
  | { type: 'error'; message: string };
